import { useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Select, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNotificationRules } from '@/entities/report/api/hooks';
import type { NotificationRule } from '@/entities/report/model/reportSchema';
import { antTablePagination } from '@/shared/constants/antTable';
import { channelTag } from '../constants';
import s from './NotificationRulesCard.module.scss';

const ruleColumns: ColumnsType<NotificationRule> = [
  { title: 'Событие', dataIndex: 'event', key: 'event', width: 200 },
  { title: 'Дней до', dataIndex: 'daysBefore', key: 'days', width: 90 },
  {
    title: 'Канал',
    dataIndex: 'channel',
    key: 'channel',
    width: 110,
    render: (c: string) => channelTag(c),
  },
  {
    title: 'Адресат',
    dataIndex: 'recipient',
    key: 'recipient',
    ellipsis: true,
    width: 160,
  },
  {
    title: 'Шаблон уведомления',
    dataIndex: 'templateText',
    key: 'templateText',
    render: (t: string) => <div className={s['root__template-text']}>{t}</div>,
  },
];

export function NotificationRulesCard() {
  const { data: rulesFromApi, isLoading: rulesLoading } = useNotificationRules();

  const [extraRules, setExtraRules] = useState<NotificationRule[]>([]);
  const [ruleModal, setRuleModal] = useState(false);
  const [form] = Form.useForm();

  const rules = useMemo(() => [...(rulesFromApi ?? []), ...extraRules], [rulesFromApi, extraRules]);

  const submitRule = () => {
    form.validateFields().then((v) => {
      setExtraRules((prev) => [
        ...prev,
        {
          id: -Date.now(),
          event: v.event as string,
          daysBefore: v.daysBefore as string,
          channel: v.channel as string,
          recipient: v.recipient as string,
          templateText: (v.templateText as string) ?? '',
        },
      ]);
      setRuleModal(false);
      form.resetFields();
      message.success('Правило добавлено');
    });
  };

  return (
    <>
      <div className={s['root__section']}>
        <div className={s['root__section-header']}>
          <div className={s['root__table-title']}>Правила уведомлений</div>
          <Button
            type="primary"
            onClick={() => {
              form.resetFields();
              setRuleModal(true);
            }}
          >
            + Правило
          </Button>
        </div>
        <Table
          loading={rulesLoading}
          dataSource={rules}
          columns={ruleColumns}
          rowKey="id"
          size="small"
          scroll={{ x: 960 }}
          pagination={{ ...antTablePagination }}
        />
      </div>

      <Modal
        title="Новое правило уведомления"
        open={ruleModal}
        onCancel={() => {
          setRuleModal(false);
          form.resetFields();
        }}
        onOk={submitRule}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="event" label="Событие" rules={[{ required: true, message: 'Укажите событие' }]}>
            <Select
              options={[
                { value: 'Истечение медосмотра', label: 'Истечение медосмотра' },
                { value: 'Истечение обучения', label: 'Истечение обучения' },
                {
                  value: 'Истечение удостоверения (3 мес.)',
                  label: 'Истечение удостоверения (3 мес.)',
                },
                { value: 'Завершение стажировки', label: 'Завершение стажировки' },
                { value: 'Истечение срока СИЗ', label: 'Истечение срока СИЗ' },
                { value: 'Пересмотр карты СОУТ', label: 'Пересмотр карты СОУТ' },
                { value: 'Пересмотр карты ОПР', label: 'Пересмотр карты ОПР' },
                { value: 'Просрочка мероприятия ОПР', label: 'Просрочка мероприятия ОПР' },
              ]}
              placeholder="Выберите событие"
            />
          </Form.Item>
          <Form.Item name="daysBefore" label="Дней до" rules={[{ required: true, message: 'Укажите интервал' }]}>
            <Input placeholder="Напр. 30, 14, 7" />
          </Form.Item>
          <Form.Item name="channel" label="Канал" rules={[{ required: true, message: 'Выберите канал' }]}>
            <Select
              options={[
                { value: 'Email', label: 'Email' },
                { value: 'Внутренние', label: 'Внутренние (в интерфейсе)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="recipient" label="Адресат" rules={[{ required: true, message: 'Укажите адресата' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="templateText"
            label="Текст шаблона уведомления"
            rules={[{ required: true, message: 'Укажите текст шаблона' }]}
            extra="Плейсхолдеры в фигурных скобках подставляются при отправке: {сотрудник}, {дата}, {рабочее_место} и т.п."
          >
            <Input.TextArea
              rows={3}
              placeholder="Например: Напоминание: у {сотрудник} срок действия истекает {дата}."
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
